import type { StateCreator } from "zustand";
import type { FormResponse } from "@tellescope/types-models";
import type { FormFieldNode } from "../utils/types";

export interface FormStoreState {
  /**
   * Files chosen for a field but not yet uploaded, keyed by field id. They live only in memory until
   * submit, so they are the one answer a draft save cannot protect — which is why hosts that can be
   * dismissed check this before closing.
   *
   * Fields with `options.autoUploadFiles` never appear here: those upload on selection and commit
   * their answer immediately, so there is nothing pending to lose.
   */
  pendingFilesByFieldId: Record<string, File[]>;
  /**
   * Value the `FORM_LOGIC_URL_PARAMETER` pseudo-field tests against, so a link can steer which
   * branch of a form the recipient sees. Named for the `?logic` query param it is persisted as.
   *
   * Only the deeplink surface sets this, and only that surface persists it — its store writes to the
   * query string, which is what lets the branch survive a copied or reopened link. In-app surfaces
   * leave it undefined.
   */
  logic?: string;
  /**
   * How far a submission has got. `uploading` covers the file uploads that precede the request,
   * `submitting` the request itself; the wizard swaps to a progress screen for either.
   */
  submitPhase: SubmitPhase;
  /** File-upload progress during the `uploading` phase, shown on that screen. */
  uploadProgress: UploadProgress;

  /**
   * The enduser's path through the wizard, oldest first; the last node is the field on screen. The
   * active field, the back stack, the set of navigated ids, and progress are all read off it.
   *
   * Stays `undefined` until the enduser actually navigates. Until then `useFormNavigation` derives
   * the opening stack from the answers loaded so far — which is how a partly-filled form reopens on
   * the first unanswered question — so `undefined` means "not yet navigated", not "nothing here".
   */
  fieldStack?: FormFieldNode[];

  /** Fields the enduser has blurred, which is what gates whether a validation error may show. */
  touchedFields: Set<string>;
  /** Text typed into the "confirm your entry" duplicate of a field, keyed by field id. */
  repeatInputs: Record<string, string>;
  repeatTouchedFields: Set<string>;

  /** Set while a field's `autoSubmit` option is driving a submission rather than the Submit button. */
  isAutoSubmitting: boolean;
  autoSubmitted: boolean;
  autoSubmitError: boolean;

  /**
   * Destination `submit_form_response` chose for this response — an integration's link via
   * `redirectTo`. Kept here rather than in the submit callback's return value because autosubmit and
   * the Submit button both produce it, and only the wizard body can act on it.
   */
  serverRedirectUrl?: string;

  /**
   * The response `submit_form_response` saved. Kept here for the same reason as
   * `serverRedirectUrl` — autosubmit discards the mutation's return value, so the thanks screen
   * would otherwise have no way to offer a copy of what was actually recorded.
   */
  submittedResponse?: FormResponse;

  /** Payment-customer id captured by a Stripe or Chargebee field for the submission that follows. */
  customerId?: string;

  /**
   * Join link for an appointment booked during this form, captured from the booking iframe's
   * `Join Link` message by an `Appointment Booking` field. Consumed after submit when the form sets
   * `redirectToBookedAppointmentOnSubmit`, which is why it is kept per-form rather than in storage:
   * the link must not outlive the form that earned it and redirect a later one.
   */
  bookedAppointmentUrl?: string;
}

export type SubmitPhase = "idle" | "uploading" | "submitting";

export interface UploadProgress {
  completed: number;
  total: number;
}

export interface FormStoreActions {
  setPendingFilesForField: (fieldId: string, files: File[]) => void;
  setLogic: (logic: string | undefined) => void;
  setSubmitPhase: (submitPhase: SubmitPhase) => void;
  setUploadProgress: (uploadProgress: UploadProgress) => void;
  clearPendingFilesForFields: (fieldIds: string[]) => void;

  setFieldStack: (fieldStack: FormFieldNode[] | undefined) => void;

  setTouchedFields: (touchedFields: Set<string>) => void;
  setRepeatInputs: (repeatInputs: Record<string, string>) => void;
  setRepeatTouchedFields: (repeatTouchedFields: Set<string>) => void;

  setIsAutoSubmitting: (isAutoSubmitting: boolean) => void;
  setAutoSubmitted: (autoSubmitted: boolean) => void;
  setAutoSubmitError: (autoSubmitError: boolean) => void;
  setServerRedirectUrl: (serverRedirectUrl: string | undefined) => void;
  setSubmittedResponse: (submittedResponse: FormResponse | undefined) => void;

  setCustomerId: (customerId: string | undefined) => void;
  setBookedAppointmentUrl: (bookedAppointmentUrl: string | undefined) => void;
  /**
   * Returns the store to its initial state.
   *
   * Call this from the events that begin a form — a host opening it, or `continueToFormInApp`
   * handing over to the next form in a chain — never from a mount effect. These stores live at
   * module scope and outlive any single form, so without an explicit reset the next form would open
   * holding the previous one's state.
   */
  reset: () => void;
}

export type FormStore = FormStoreState & FormStoreActions;

const initialState: FormStoreState = {
  pendingFilesByFieldId: {},
  submitPhase: "idle",
  uploadProgress: { completed: 0, total: 0 },
  touchedFields: new Set(),
  repeatInputs: {},
  repeatTouchedFields: new Set(),
  isAutoSubmitting: false,
  autoSubmitted: false,
  autoSubmitError: false,
};

/**
 * The state and behavior every form store shares.
 *
 * Each surface that renders a form — the `/link/form` deeplink, the in-app dialog wizard, a public
 * form page — declares its **own** store from this creator, with its own name and its own
 * persistence backend. They are separate stores on purpose: two forms can be open at once, and a
 * single shared store would let them overwrite each other.
 */
export const createFormStoreState: StateCreator<FormStore> = (set) => ({
  ...initialState,

  setLogic: (logic) => set({ logic }),

  setSubmitPhase: (submitPhase) => set({ submitPhase }),

  setUploadProgress: (uploadProgress) => set({ uploadProgress }),

  setFieldStack: (fieldStack) => set({ fieldStack }),

  setTouchedFields: (touchedFields) => set({ touchedFields }),

  setRepeatInputs: (repeatInputs) => set({ repeatInputs }),

  setRepeatTouchedFields: (repeatTouchedFields) => set({ repeatTouchedFields }),

  setIsAutoSubmitting: (isAutoSubmitting) => set({ isAutoSubmitting }),

  setAutoSubmitted: (autoSubmitted) => set({ autoSubmitted }),

  setAutoSubmitError: (autoSubmitError) => set({ autoSubmitError }),

  setServerRedirectUrl: (serverRedirectUrl) => set({ serverRedirectUrl }),

  setSubmittedResponse: (submittedResponse) => set({ submittedResponse }),

  setCustomerId: (customerId) => set({ customerId }),

  setBookedAppointmentUrl: (bookedAppointmentUrl) => set({ bookedAppointmentUrl }),

  setPendingFilesForField: (fieldId, files) =>
    set((state) => ({
      pendingFilesByFieldId: { ...state.pendingFilesByFieldId, [fieldId]: files },
    })),

  clearPendingFilesForFields: (fieldIds) =>
    set((state) => ({
      pendingFilesByFieldId: fieldIds.reduce(
        (acc, fieldId) => ({ ...acc, [fieldId]: [] }),
        state.pendingFilesByFieldId
      ),
    })),

  // The optional keys are listed explicitly: `set` merges, so omitting them would leave the previous
  // form's navigation position and customer id in place.
  //
  // `logic` deliberately survives: it comes from the link that opened this surface rather than from
  // the form being filled, so clearing it here would drop the branch the recipient was sent to.
  reset: () =>
    set({
      ...initialState,
      fieldStack: undefined,
      customerId: undefined,
      serverRedirectUrl: undefined,
      submittedResponse: undefined,
      bookedAppointmentUrl: undefined,
    }),
});

/**
 * True while any field holds files that have been chosen but not yet uploaded.
 *
 * Takes the map rather than the whole store so it composes with a shallow key read:
 * `selectHasPendingFiles(useFormShallowStore("pendingFilesByFieldId").pendingFilesByFieldId)`.
 */
export const selectHasPendingFiles = (pendingFilesByFieldId: FormStoreState["pendingFilesByFieldId"]): boolean =>
  Object.values(pendingFilesByFieldId).some((files) => files.length > 0);
